<script setup>
import { computed } from 'vue';

import { HrIconBtn } from '../primitives';

/**
 * Icon button that switches between selected and unselected visual states.
 *
 * @status recommended
 * @group actions
 * @useWhen Representing a binary toggle where the current state must be visually communicated (e.g. bookmark,
 * favorite, mute)
 * @avoidWhen The button triggers an action with no persistent state — use HrIconActionBtn
 * @related HrIconActionBtn
 * @a11y Sets aria-pressed to communicate the selected state to assistive technologies. Supply aria-label
 * describing the toggle.
 */
defineOptions({ name: 'HrIconToggleBtn' });

const props = defineProps({
  /**
   * Icon shown in the unselected state.
   * @example bookmark_border
   */
  icon: { type: String, required: true },
  /** Icon shown in the selected state. Falls back to `icon` when omitted. */
  selectedIcon: { type: String, default: 'bookmark' },
  /** Whether the button is in the selected (on) state. Supports v-model:selected. */
  selected: { type: Boolean, default: false },
  /** Shows a loading spinner in place of the icon. */
  loading: { type: Boolean, default: false },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: { type: Boolean, default: false },
  /** Renders a skeleton placeholder instead of the button. */
  pending: { type: Boolean, default: false },
  /** Vue Router route location. Renders the button as a router-link. */
  to: { type: [String, Object], default: null },
  /** Replace the current history entry instead of pushing a new one. */
  replace: { type: Boolean, default: false },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: { type: String, default: null },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: { type: String, default: null },
  /**
   * Quasar visual props forwarded to the underlying icon button. Ignored in the design-system theme.
   * The color shifts to primary when selected.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: { type: Object, default: () => ({}) },
});

const emit = defineEmits({
  /** Emitted when the user activates the button; carries the new boolean `selected` value. */
  'update:selected': null,
});

// v-bind="$props" is intentionally avoided: selected and selectedIcon must not be forwarded to HrIconBtn.
const effectiveIcon = computed(() => (props.selected && props.selectedIcon !== null ? props.selectedIcon : props.icon));

// @todo Remove legacy-props when Legacy theme is removed
const mergedLegacyProps = computed(() => ({
  flat: true,
  color: props.selected ? 'primary' : 'text-dark-variant',
  ...props.legacyProps,
}));
</script>

<template>
  <hr-icon-btn
    :icon="effectiveIcon"
    :variant="selected ? 'tonal' : 'standard'"
    :disable="disable"
    :loading="loading"
    :pending="pending"
    :to="to"
    :replace="replace"
    :href="href"
    :target="target"
    :aria-pressed="selected"
    :legacy-props="mergedLegacyProps"
    @click="emit('update:selected', !selected)"
  />
</template>
